// src/features/alerts/AlertShow.tsx

import React from 'react';
import {
  Show,
  TextField,
  DateField,
  RichTextField,
  TabbedShowLayout,
  Tab,
  useRecordContext,
  useTranslate,
} from 'react-admin';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { MyFormContainer } from '@/components/forms/MyFormContainer';
import { MyFormSection } from '@/components/forms/MyFormLayout';
import { AlertStatusChip } from './components/AlertStatusChip';
import { AlertSeverityBadge } from './components/AlertSeverityBadge';
import { SendAlertButton } from './components/SendAlertButton';

/**
 * Known context keys map to a translated label; anything unmapped is
 * humanised from the key instead of shown verbatim (F-B-018). The raw
 * shape of `context` is not modelled anywhere in the codebase (free-form
 * per alert type), so unknown keys always fall back gracefully.
 */
const CONTEXT_KEY_TRANSLATION_KEYS: Record<string, string> = {
  device: 'resources.alerts.show.contextFields.device',
  deviceId: 'resources.alerts.show.contextFields.device',
  hotel: 'resources.alerts.show.contextFields.hotel',
  hotelId: 'resources.alerts.show.contextFields.hotel',
  section: 'resources.alerts.show.contextFields.section',
  sectionCode: 'resources.alerts.show.contextFields.section',
  reason: 'resources.alerts.show.contextFields.reason',
  timestamp: 'resources.alerts.show.contextFields.timestamp',
  occurredAt: 'resources.alerts.show.contextFields.timestamp',
};

const humanizeContextKey = (key: string, translate: (k: string, opts?: any) => string): string => {
  const translationKey = CONTEXT_KEY_TRANSLATION_KEYS[key];
  if (translationKey) return translate(translationKey);
  return key
    .replace(/[_-]/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
};

const formatContextValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

/**
 * Single labelled row for a context field
 */
const ContextRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 1 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ fontWeight: 500, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Box>
);

/**
 * Alert header with status badges
 */
const AlertHeader: React.FC = () => {
  const record = useRecordContext();
  
  if (!record) return null;

  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
      <AlertSeverityBadge severity={record.severity} />
      <AlertStatusChip isSent={record.isSent} />
    </Box>
  );
};

/**
 * Alert basic information section
 */
const AlertBasicInfo: React.FC = () => {
  const translate = useTranslate();

  return (
    <MyFormSection
      title={translate('resources.alerts.show.sections.info')}
      icon={<InfoIcon />}
      variant="outlined"
      maxContentWidth="100%"
    >
      <TextField source="alertType" label={translate('resources.alerts.fields.alertType')} />
      <TextField source="title" label={translate('resources.alerts.fields.title')} />
      <RichTextField source="message" label={translate('resources.alerts.fields.message')} />
    </MyFormSection>
  );
};

/**
 * Alert location section (hotel and device)
 */
const AlertLocationInfo: React.FC = () => {
  const record = useRecordContext();
  const translate = useTranslate();

  if (!record?.hotelId && !record?.deviceId) {
    return null;
  }

  return (
    <MyFormSection
      title={translate('resources.alerts.show.sections.location')}
      icon={<InfoIcon />}
      variant="outlined"
      maxContentWidth="100%"
    >
      {record.hotelId && <TextField source="hotelId" label={translate('resources.alerts.fields.hotelId')} />}
      {record.deviceId && <TextField source="deviceId" label={translate('resources.alerts.fields.deviceId')} />}
    </MyFormSection>
  );
};

/**
 * Alert timestamps section
 */
const AlertTimestamps: React.FC = () => {
  const translate = useTranslate();

  return (
    <MyFormSection
      title={translate('resources.alerts.show.sections.timeline')}
      icon={<ScheduleIcon />}
      variant="outlined"
      maxContentWidth="100%"
    >
      <DateField source="createdAt" label={translate('resources.alerts.fields.createdAt')} showTime />
      <DateField source="sentAt" label={translate('resources.alerts.fields.sentAt')} showTime emptyText={translate('resources.alerts.show.notSentYet')} />
    </MyFormSection>
  );
};

/**
 * Alert context section (JSON data)
 */
const AlertContextSection: React.FC = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  const theme = useTheme();

  if (!record?.context) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          {translate('resources.alerts.show.contextEmpty')}
        </Typography>
      </Box>
    );
  }

  const entries = Object.entries(record.context as Record<string, unknown>);

  return (
    <MyFormSection
      title={translate('resources.alerts.show.sections.context')}
      subtitle={translate('resources.alerts.show.sections.contextSubtitle')}
      icon={<InfoIcon />}
      variant="outlined"
      maxContentWidth="100%"
    >
      {entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {translate('resources.alerts.show.contextEmpty')}
        </Typography>
      ) : (
        <Grid container spacing={1}>
          {entries.map(([key, value]) => (
            <Grid item xs={12} sm={6} key={key}>
              <ContextRow label={humanizeContextKey(key, translate)} value={formatContextValue(value)} />
            </Grid>
          ))}
        </Grid>
      )}

      <Accordion
        disableGutters
        elevation={0}
        square
        sx={{
          mt: 2,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2" color="text.secondary">
            {translate('resources.alerts.show.viewTechnicalData')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <pre style={{
            overflow: 'auto',
            padding: '16px',
            backgroundColor: theme.palette.grey[50],
            borderRadius: '4px',
            margin: 0,
            fontSize: '0.875rem',
            lineHeight: 1.6,
          }}>
            {JSON.stringify(record.context, null, 2)}
          </pre>
        </AccordionDetails>
      </Accordion>
    </MyFormSection>
  );
};

/**
 * Alert actions section
 */
const AlertActions: React.FC = () => {
  const record = useRecordContext();

  if (!record || record.isSent) {
    return null;
  }

  return (
    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
      <SendAlertButton />
    </Box>
  );
};

/**
 * Alert Show
 * Detailed view of an alert with improved aesthetics
 * SuperAdmin only
 */
export const AlertShow: React.FC = () => {
  return (
    <Show title=" ">
      <MyFormContainer maxWidth="lg">
        <TabbedShowLayout>
          <Tab label="resources.alerts.tabs.details">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Status badges at the top */}
              <AlertHeader />

              {/* Basic information */}
              <AlertBasicInfo />

              {/* Location (hotel/device) */}
              <AlertLocationInfo />

              {/* Timestamps */}
              <AlertTimestamps />

              {/* Actions (send button if pending) */}
              <AlertActions />
            </Box>
          </Tab>

          <Tab label="resources.alerts.tabs.context">
            <AlertContextSection />
          </Tab>
        </TabbedShowLayout>
      </MyFormContainer>
    </Show>
  );
};